import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import AppShell from "@/components/AppShell";
import DismissibleIntro from "@/components/DismissibleIntro";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import {
  DownloadSimple, Trash, House, Spinner, Image as ImageIcon,
  ArrowLeft, Check, PencilSimple, Plus, ArrowsOutSimple, Sparkle,
  ArrowsClockwise,
} from "@phosphor-icons/react";
import { shareImage } from "@/lib/share";
import { PhotoTileIcon } from "@/components/photo/FotoLightboxParts";
import { GalleriaLightbox } from "@/components/photo/GalleriaLightbox";
import AdHocSocialModal from "@/components/AdHocSocialModal";

/**
 * GalleriaFoto — vista completa di TUTTE le foto lavorate dall'agente.
 *
 * Differenza da `FotoEditor.jsx`: lì la "tua galleria" mostra solo le ultime
 * lavorazioni come reminder. Qui c'è tutto: foto da live preview + foto
 * associate ad annunci, con possibilità di cancellare, scaricare, condividere
 * e usare in un annuncio. Pensato per la gestione "post produzione" della
 * libreria di asset.
 */
export default function GalleriaFoto() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // iter 255 — Picker mode: quando arriviamo da un PropertyDetail con
  // ?import={propertyId} la galleria diventa un selettore multi-foto per
  // importarle TUTTE in quell'annuncio. Niente Share/Delete/dropdown.
  const importPropertyId = searchParams.get("import");
  // iter v25 — `type=plan` quando arriva dalla Pianta tab → import as planimetria
  const isPlanPicker = searchParams.get("type") === "plan";
  const isPicker = !!importPropertyId;
  const [photos, setPhotos] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState({});
  const [importingId, setImportingId] = useState(null);
  const [importBusyPid, setImportBusyPid] = useState(null);
  const [filter, setFilter] = useState(isPicker ? "live" : "all"); // all | live | property
  const [selected, setSelected] = useState({});  // id -> true for multi-select
  // Picker mode: batch import progress
  const [batchImporting, setBatchImporting] = useState(false);
  // iter 275: modale di conferma cancellazione (sostituisce `confirm()` nativo
  // che su iOS Safari/PWA viene spesso bloccato silenziosamente — risultato:
  // l'utente clicca "Cancella" e non succede nulla). Vedi screen recording
  // utente 13 mag 2026.
  // shape: null | { mode: "one"|"bulk", id?, source?, count? }
  const [confirmDelete, setConfirmDelete] = useState(null);

  // iter 275 round 30: lightbox identico al FotoEditor (squircle iOS +
  // tap-and-hold per Prima/Dopo). Doppia <img> sovrapposta per zero flicker.
  const [lightboxItem, setLightboxItem] = useState(null);
  // iter v29 r247 — lightboxView deriva dall'item (default "after"), non da
  // useEffect → set-state-in-effect rimosso. Quando l'item cambia, view torna
  // automaticamente ad "after" grazie a `key={lightboxItem?.id}` sull'Item.
  const [lightboxView, setLightboxView] = useState("after");
  // iter 313 r3 — UX stile FotoTab: check circle SEMPRE visibile in top-left,
  // tap sulla foto = lightbox, tap sul check = seleziona/deseleziona. Niente
  // più `selectMode` esplicita richiesta dall'utente. Long-press resta come
  // scorciatoia mobile per selezionare velocemente.
  const longPressTimer = React.useRef(null);
  // iter v29 r247 — `selectMode` ora derivato (vedi sotto), non più useState.
  // Per "uscire" basta deselezionare tutto (setSelected({})).
  // iter 275 round 32: bulk download ZIP
  const [bulkDownloading, setBulkDownloading] = useState(false);
  // iter 275 round 51: modale Social senza annuncio
  const [adHocOpen, setAdHocOpen] = useState(false);
  useEffect(() => {
    if (!lightboxItem) return;
    const onKey = (e) => { if (e.key === "Escape") setLightboxItem(null); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightboxItem]);

  // iter v29 r247 — Wrapper per aprire il lightbox; resetta view ad "after"
  // in modo dichiarativo (no setState dentro useEffect).
  const openLightbox = (item) => {
    setLightboxView("after");
    setLightboxItem(item);
  };

  const importProperty = useMemo(
    () => properties.find((p) => p.id === importPropertyId),
    [properties, importPropertyId],
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Riusa lo stesso endpoint di FotoEditor "history" + lista properties
      const [gal, props] = await Promise.all([
        api.get("/gallery", { params: { limit: 200 } }),
        api.get("/properties"),
      ]);
      // /gallery normalised: {id, source, kind, url, property_id, property_title, created_at}
      setPhotos(gal.data?.items || gal.data || []);
      setProperties((props.data?.items || props.data || []).filter((p) => !p.is_demo));
    } catch (e) {
      toast.error(apiError(e, "Impossibile caricare la galleria"));
    } finally {
      setLoading(false);
    }
  }, []);

  // iter v29 r247 — load() chiama setLoading/setPhotos/setProperties; il
  // lint set-state-in-effect è troppo restrittivo per questo pattern di
  // fetch on mount (use case standard, non c'è alternativa pulita).
  // eslint-disable-next-line react-hooks/set-state-in-effect
  useEffect(() => { load(); }, [load]);

  // iter v29 r247 — Filter inizializzato da useState(isPicker ? "live" : "all"),
  // niente useEffect → niente set-state-in-effect.

  // Picker mode: batch-import N foto selezionate nell'annuncio target,
  // poi torna al PropertyDetail tab "foto".
  const importBatchToProperty = async () => {
    const ids = Object.keys(selected).filter((k) => selected[k]);
    if (ids.length === 0 || !importPropertyId) return;
    setBatchImporting(true);
    let ok = 0, fail = 0;
    for (const id of ids) {
      try {
        const realId = id.includes(":") ? id.split(":", 2)[1] : id;
        await api.post(`/properties/${importPropertyId}/photos/import-from-live`, {
          live_id: realId,
          as_plan: isPlanPicker,
        });
        ok++;
      } catch (_e) {
        fail++;
      }
    }
    setBatchImporting(false);
    if (ok > 0) {
      toast.success(`${ok} ${isPlanPicker ? (ok === 1 ? "pianta aggiunta" : "piante aggiunte") : (ok === 1 ? "foto aggiunta" : "foto aggiunte")} all'annuncio${fail ? ` · ${fail} errori` : ""}`);
      navigate(`/dashboard/property/${importPropertyId}?tab=${isPlanPicker ? "pianta" : "foto"}`);
    } else {
      toast.error("Importazione fallita");
    }
  };

  const filtered = photos.filter((p) => {
    if (filter === "all") return true;
    if (filter === "live") return p.source === "live";
    if (filter === "property") return p.source === "property";
    return true;
  });

  const deleteOne = async (id, source) => {
    // iter 275: usa modale React invece di confirm() nativo (bloccato su iOS).
    setConfirmDelete({ mode: "one", id, source });
  };

  const _doDeleteOne = async (id, source) => {
    setDeleting((d) => ({ ...d, [id]: true }));
    try {
      // iter 242f: id da /gallery è prefixato ("live:xxx" o "prop:xxx").
      // Strippiamo il prefix e chiamiamo l'endpoint giusto per ciascun source.
      const realId = id.includes(":") ? id.split(":", 2)[1] : id;
      if (source === "live") {
        await api.delete(`/live/${realId}`);
      } else if (source === "property") {
        // Property photo: cancella tramite endpoint photos
        await api.delete(`/photos/${realId}`);
      } else {
        toast.error("Tipo foto sconosciuto");
        return;
      }
      setPhotos((ps) => ps.filter((p) => (p.id || p.photo_id) !== id));
      setSelected((s) => { const x = { ...s }; delete x[id]; return x; });
      toast.success("Foto cancellata");
    } catch (e) {
      toast.error(apiError(e, "Errore cancellazione"));
    } finally {
      setDeleting((d) => { const x = { ...d }; delete x[id]; return x; });
    }
  };

  const deleteSelected = () => {
    const ids = Object.keys(selected).filter((k) => selected[k]);
    if (ids.length === 0) return;
    // iter 275: modale React invece di confirm() nativo.
    setConfirmDelete({ mode: "bulk", count: ids.length });
  };

  // iter 275 round 38: download multi-foto.
  // - 1 foto selezionata → download singolo file (no ZIP, è meglio)
  // - 2+ foto → ZIP server-side
  const downloadSelectedZip = async () => {
    const ids = Object.keys(selected).filter((k) => selected[k]);
    if (ids.length === 0) return;

    // Single-photo shortcut: scarica direttamente il file.
    // iter v28 r472 — device-aware: mobile → shareImage (unico modo per
    // finire in Galleria Foto su iOS), desktop → download diretto.
    if (ids.length === 1) {
      const id = ids[0];
      const item = photos.find((p) => (p.id || p.photo_id) === id);
      if (item) {
        const src = item.url || item.enhanced_url || item.image_url;
        const filename = `pronto-${id.replace(/^(live|prop):/, "").slice(0, 8)}.jpg`;
        const isMobile = typeof navigator !== "undefined" && /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent);
        if (isMobile) {
          await shareImage({ url: src, filename, title: "", text: "" });
          return;
        }
        try {
          const res = await fetch(src);
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const blob = await res.blob();
          const blobUrl = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = blobUrl;
          a.download = filename;
          a.rel = "noopener";
          document.body.appendChild(a);
          a.click();
          a.remove();
          setTimeout(() => URL.revokeObjectURL(blobUrl), 1500);
          toast.success("Foto salvata");
        } catch {
          toast.error("Download non riuscito");
        }
        return;
      }
    }

    setBulkDownloading(true);
    try {
      const res = await api.post(
        "/gallery/bulk-download",
        { ids },
        { responseType: "blob" }
      );
      const blob = new Blob([res.data], { type: "application/zip" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const ts = new Date().toISOString().replace(/[-:T]/g, "").slice(0, 14);
      a.download = `pronto-foto-${ts}.zip`;
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1500);
      const added = parseInt(res.headers?.["x-pronto-added"] || ids.length, 10);
      const skipped = parseInt(res.headers?.["x-pronto-skipped"] || 0, 10);
      if (skipped > 0) {
        toast.success(`ZIP scaricato · ${added} foto · ${skipped} saltate`);
      } else {
        toast.success(`ZIP scaricato · ${added} foto`);
      }
    } catch (e) {
      toast.error(apiError(e, "Impossibile generare lo ZIP"));
    } finally {
      setBulkDownloading(false);
    }
  };

  const _doDeleteSelected = async () => {
    const ids = Object.keys(selected).filter((k) => selected[k]);
    if (ids.length === 0) return;
    let ok = 0, fail = 0;
    for (const id of ids) {
      try {
        const p = photos.find((x) => (x.id || x.photo_id) === id);
        const realId = id.includes(":") ? id.split(":", 2)[1] : id;
        const source = p?.source || (id.startsWith("prop:") ? "property" : "live");
        if (source === "live") {
          await api.delete(`/live/${realId}`);
        } else {
          await api.delete(`/photos/${realId}`);
        }
        ok++;
      } catch (_e) {
        fail++;
      }
    }
    toast.success(`Cancellate ${ok}${fail ? ` · ${fail} errori` : ""}`);
    setSelected({});
    load();
  };

  const importToProperty = async (photoId, pid) => {
    setImportBusyPid(pid);
    try {
      // iter 242f: strip prefix "live:" se presente
      const realLiveId = photoId.includes(":") ? photoId.split(":", 2)[1] : photoId;
      await api.post(`/properties/${pid}/photos/import-from-live`, { live_id: realLiveId });
      toast.success("Foto aggiunta all'annuncio");
      setImportingId(null);
      load();
    } catch (e) {
      toast.error(apiError(e, "Impossibile aggiungere"));
    } finally {
      setImportBusyPid(null);
    }
  };

  const selectedCount = Object.values(selected).filter(Boolean).length;
  const toggleSelected = (id) => setSelected((s) => ({ ...s, [id]: !s[id] }));

  // iter 313 r3 — quando la prima foto viene selezionata, entra automaticamente
  // in select mode (per mostrare la barra azioni). Quando torna a 0, esce.
  // iter v29 r247 — derivato direttamente da selectedCount + isPicker invece
  // di setState dentro useEffect (fix lint react-hooks/set-state-in-effect).
  // Picker mode tiene SEMPRE selectMode attivo (l'utente sta scegliendo cosa importare).
  const selectMode = isPicker || selectedCount > 0;

  // Picker mode: "Modifica" porta al FotoEditor con la foto pre-caricata
  // (via query ?reedit=ID) e link di ritorno al picker dell'annuncio.
  // L'utente sceglie modalità (Automatico/Scelgo io/Fedele) lì, poi torna qui.
  const editAndAdd = (photoId) => {
    const realId = photoId.includes(":") ? photoId.split(":", 2)[1] : photoId;
    navigate(`/dashboard/foto?reedit=${realId}&return=${encodeURIComponent(`/dashboard/galleria?import=${importPropertyId}`)}`);
  };

  return (
    <AppShell title={isPicker ? "Aggiungi foto all'annuncio" : "Galleria foto"} breadcrumb={isPicker ? "Aggiungi foto" : "Galleria foto"}>
      <div className="space-y-5 pb-32">
        <div>
          <button
            onClick={() => isPicker
              ? navigate(`/dashboard/property/${importPropertyId}?tab=foto`)
              : navigate("/dashboard/foto")}
            className="text-[12px] text-[#6B6A65] hover:text-[#1C1C1A] inline-flex items-center gap-1 mb-2"
            data-testid="gallery-back-to-editor"
          >
            <ArrowLeft size={12} /> {isPicker ? "Torna all'annuncio" : "Editor foto"}
          </button>
          {isPicker ? (
            <>
              <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#7C8B6E] mb-1">
                Aggiungi foto all&apos;annuncio
              </div>
              <h1 className="text-2xl sm:text-3xl font-serif text-[#1C1C1A] leading-tight">
                {importProperty?.title || "Annuncio"}
              </h1>
              <p className="text-[13px] text-[#6B6A65] mt-1">
                Tocca le foto che vuoi aggiungere. Puoi anche rimodificarle prima di importarle.
              </p>
            </>
          ) : (
            <>
              <h1 className="text-2xl sm:text-3xl font-serif text-[#1C1C1A] leading-tight">Galleria foto</h1>
              <DismissibleIntro storageKey="galleria:intro">
                <p className="text-[13px] text-[#6B6A65] mt-1">
                  Tutte le foto che hai lavorato con Pronto. Scarica, condividi, riutilizza o cancella.
                </p>
              </DismissibleIntro>
            </>
          )}
        </div>

        {/* Toolbar — nascosta in modalità picker (filtro sempre "live") */}
        {!isPicker && (
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="inline-flex bg-[#F1F4ED] rounded-md p-1 text-[12px]" data-testid="gallery-filter">
            {[
              { v: "all", l: `Tutte (${photos.length})` },
              { v: "live", l: `Sciolte (${photos.filter((p) => !p.property_id).length})` },
              { v: "property", l: `In annunci (${photos.filter((p) => !!p.property_id).length})` },
            ].map(({ v, l }) => (
              <button
                key={v}
                onClick={() => setFilter(v)}
                className={`px-3 py-1.5 rounded font-medium transition-colors ${
                  filter === v ? "bg-white text-[#1C1C1A] shadow-sm" : "text-[#6B6A65] hover:text-[#1C1C1A]"
                }`}
                data-testid={`gallery-filter-${v}`}
              >
                {l}
              </button>
            ))}
          </div>

          {/* iter v26 r6 — Bulk bar spostata in STICKY BOTTOM (vedi sotto
              al return) per coerenza con la Galleria Annuncio (FotoTab). */}

          {/* iter v26 r8 — Bottone "Seleziona" per attivare manualmente la
              selezione multipla. User feedback: il cerchiolino fisso su
              ogni foto era confuso → ora di default tap = lightbox, e per
              selezione multipla l'agente preme questo bottone (oppure fa
              long-press su una foto). Quando in selectMode, il bottone
              diventa "Annulla" per uscire. */}
          {filtered.length > 0 && (
            <button
              onClick={() => {
                // iter v29 r247 — selectMode è derivato: per uscire basta
                // deselezionare; per entrare basta selezionare. Il click sul
                // bottone seleziona la prima foto in cima (se nessuna selezionata).
                if (selectMode) {
                  setSelected({});
                } else if (filtered.length > 0) {
                  setSelected({ [filtered[0].id || filtered[0].photo_id]: true });
                }
              }}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-semibold transition-colors ${
                selectMode
                  ? "bg-[#1C1C1A] text-white hover:bg-black"
                  : "bg-white border border-[#EAE7DF] text-[#1C1C1A] hover:border-[#7C8B6E] hover:bg-[#F1F4ED]"
              }`}
              data-testid="gallery-toggle-select-mode"
            >
              <Check size={12} weight="bold" /> {selectMode ? "Annulla" : "Seleziona"}
            </button>
          )}
        </div>
        )}

        {/* Grid */}
        {loading ? (
          <div className="py-20 flex items-center justify-center">
            <Spinner size={20} className="animate-spin text-[#7C8B6E]" />
          </div>
        ) : filtered.length === 0 ? (
          <EmptyState onNew={() => navigate("/dashboard/foto")} />
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="gallery-grid">
            {filtered.map((h) => {
              const realId = h.id || h.photo_id;
              // iter v29 r247 — User M1402: thumb 480px lato server per la grid
              // (cache). Lightbox e download usano comunque l'URL originale.
              const imgSrc = h.thumb_url || h.url || h.enhanced_url || h.image_url;
              const isLive = h.source === "live" || !h.property_id;
              const isSel = !!selected[realId];
              const inSelectMode = selectMode || isPicker;

              // iter 275 round 38: long-press (450ms) entra in selectMode +
              // seleziona la foto premuta. UX standard iOS Photos.
              // iter v33 r54 · User: «basta che clicco sulla foto e mi si
              // apre». Root cause: iOS sintetizza mousedown/mouseup DOPO
              // touchend, e `onMouseDown={startLongPress}` faceva ripartire
              // un timer 450ms → il flag `longPressTimer.current !== null`
              // dentro il click bloccava l'apertura del lightbox.
              // Fix: solo handler touch (l'unico dispositivo dove serve il
              // long-press). Su desktop il tap normale apre il lightbox
              // istantaneamente.
              const startLongPress = () => {
                if (inSelectMode) return; // già in select
                longPressTimer.current = setTimeout(() => {
                  // iter v29 r247 — selectMode è derivato: basta selezionare
                  // la foto premuta, selectMode diventa true automaticamente.
                  toggleSelected(realId);
                  longPressTimer.current = null;
                }, 450);
              };
              const cancelLongPress = () => {
                if (longPressTimer.current) {
                  clearTimeout(longPressTimer.current);
                  longPressTimer.current = null;
                }
              };

              return (
                <div
                  key={realId}
                  onClick={() => {
                    if (longPressTimer.current !== null) return;
                    // iter 313 r3 — comportamento stile FotoTab:
                    //  • picker mode → tap foto = toggle (modalità import)
                    //  • selectMode attiva (≥1 già selezionata) → tap = toggle
                    //  • altrimenti → tap = apre lightbox (default)
                    if (isPicker || selectMode) {
                      toggleSelected(realId);
                    } else {
                      openLightbox(h);
                    }
                  }}
                  onTouchStart={startLongPress}
                  onTouchEnd={cancelLongPress}
                  onTouchCancel={cancelLongPress}
                  onTouchMove={cancelLongPress}
                  onContextMenu={(e) => e.preventDefault()}
                  className={`relative aspect-[4/3] rounded-md overflow-hidden bg-[#F1F4ED] group border-2 transition-all cursor-pointer ${
                    isSel ? "border-[#7C8B6E] ring-2 ring-[#7C8B6E]/30" : "border-transparent hover:border-[#7C8B6E]/40"
                  }`}
                  style={{ touchAction: "manipulation", WebkitTouchCallout: "none" }}
                  data-testid={`gallery-photo-${realId}`}
                  data-selected={isSel ? "true" : "false"}
                >
                  <img src={imgSrc} alt="" className="w-full h-full object-contain bg-[#EFECE4]" loading="lazy" draggable={false} />

                  {/* iter v26 r8 — check circle visibile SOLO in selectMode.
                      User feedback: il cerchiolino sempre visibile crea due
                      barre confuse (sticky bottom + lightbox actions). Ora
                      default = solo icona apertura lightbox a destra; per
                      selezione multipla → long-press OPPURE bottone
                      "Seleziona" nella toolbar in alto. */}
                  {inSelectMode && (
                    <button
                      onClick={(e) => { e.stopPropagation(); toggleSelected(realId); }}
                      className={`absolute top-1.5 left-1.5 w-5 h-5 aspect-square shrink-0 rounded-full inline-flex items-center justify-center transition-colors shadow-sm p-0 ${
                        isSel
                          ? "bg-[#7C8B6E] text-white border-2 border-white"
                          : "bg-white/90 text-[#7C8B6E]/0 border-2 border-white/95 hover:text-[#7C8B6E]/35"
                      }`}
                      style={{ minWidth: "20px", minHeight: "20px", width: "20px", height: "20px" }}
                      data-testid={`gallery-select-${realId}`}
                      aria-label={isSel ? "Deseleziona" : "Seleziona"}
                    >
                      <Check size={11} weight="bold" />
                    </button>
                  )}

                  {/* Badge "in annuncio" — sposto in basso per liberare top-right per i pulsantini */}
                  {!isLive && h.property_id && !isPicker && (
                    <div className="absolute bottom-1.5 left-1.5 px-1.5 py-0.5 bg-black/60 text-white text-[9px] uppercase tracking-wide rounded">
                      In annuncio
                    </div>
                  )}

                  {/* iter v28 r459 — Countdown 30 giorni per le foto live.
                      User M1376: «le foto si conservano per 30 giorni dopo
                      di che si eliminano». Solo se < 7 giorni mostriamo il
                      badge in rosso/ambra per non infastidire ogni volta. */}
                  {isLive && h.auto_expires && typeof h.days_left === "number" && h.days_left <= 7 && !isPicker && (
                    <div
                      className={`absolute top-1.5 left-1.5 px-1.5 py-0.5 text-[9px] uppercase tracking-wide rounded font-bold shadow-md ${
                        h.days_left <= 2 ? "bg-red-600 text-white" : "bg-amber-100 text-amber-900"
                      }`}
                      data-testid={`gallery-expires-${realId}`}
                      title="Scarica la foto prima che venga eliminata"
                    >
                      {h.days_left === 0 ? "Sparisce oggi" : h.days_left === 1 ? "Sparisce domani" : `Sparisce in ${h.days_left}g`}
                    </div>
                  )}

                  {/* iter 275 round 34: squircle iOS — nascosta in selectMode
                      (selezione foto, non azione singola). */}
                  {!isPicker && !selectMode && <PhotoTileIcon Icon={ArrowsOutSimple} />}

                  {/* Overlay azioni · Picker mode: solo "Modifica" */}
                  {isPicker && isSel && (
                    <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/75 to-transparent p-2 flex items-center justify-end">
                      <button
                        onClick={(e) => { e.stopPropagation(); editAndAdd(realId); }}
                        className="text-[11px] bg-white text-[#1C1C1A] font-semibold px-2.5 py-1 rounded-full inline-flex items-center gap-1 hover:bg-[#F1F4ED] shadow-md"
                        data-testid={`gallery-edit-${realId}`}
                        title="Rimodifica prima di aggiungere"
                      >
                        <PencilSimple size={11} weight="bold" /> Modifica
                      </button>
                    </div>
                  )}

                  {/* Modal "Usa in annuncio" — solo non-picker */}
                  {!isPicker && importingId === realId && (
                    <div
                      className="absolute inset-0 bg-black/55 backdrop-blur-[2px] flex items-center justify-center p-2 z-10"
                      onClick={(e) => { e.stopPropagation(); setImportingId(null); }}
                    >
                      <div
                        className="bg-white rounded-lg shadow-2xl p-3 w-full max-w-[240px]"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="text-[12px] font-semibold text-[#1C1C1A] mb-2">Usa in…</div>
                        <div className="space-y-1 max-h-[180px] overflow-y-auto">
                          {properties.slice(0, 8).map((p) => (
                            <button
                              key={p.id}
                              onClick={() => importToProperty(realId, p.id)}
                              disabled={importBusyPid === p.id}
                              className="w-full text-left px-2 py-1.5 text-[12px] bg-[#FAF9F5] hover:bg-[#F1F4ED] rounded inline-flex items-center gap-1.5"
                            >
                              {importBusyPid === p.id ? <Spinner size={11} className="animate-spin" /> : <House size={11} weight="fill" className="text-[#7C8B6E]" />}
                              <span className="truncate">{p.title || "Senza titolo"}</span>
                            </button>
                          ))}
                        </div>
                        <button onClick={() => setImportingId(null)} className="w-full mt-2 text-[11px] text-[#9A968B]">Annulla</button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* iter v26 r6 — STICKY BULK BAR (NON-picker mode).
          Stesso pattern della Galleria Annuncio (FotoTab) per coerenza:
          appare in basso quando c'è selezione, niente più overflow inline
          accanto ai filtri. Mantenuta separata dal picker mode (sotto)
          che ha una sua bar dedicata con bottone "Aggiungi all'annuncio". */}
      {!isPicker && selectedCount > 0 && (
        <>
          <div aria-hidden="true" className="h-24" data-testid="gallery-bulk-bar-spacer" />
          <div
            className="fixed left-2 right-2 z-50 px-3 py-2.5 bg-[#1C1C1A] text-white rounded-2xl shadow-2xl flex items-center gap-2 flex-wrap animate-in slide-in-from-bottom-4 duration-200"
            style={{ bottom: "calc(0.75rem + env(safe-area-inset-bottom, 0px))" }}
            data-testid="gallery-bulk-bar"
          >
            <span className="text-[12.5px] font-semibold flex-shrink-0 inline-flex items-center gap-2">
              <span
                className="w-5 h-5 aspect-square shrink-0 rounded-full bg-[#7C8B6E] inline-flex items-center justify-center text-[10px] font-bold"
                style={{ minWidth: "20px", minHeight: "20px", width: "20px", height: "20px" }}
              >{selectedCount}</span>
              <span className="hidden sm:inline">{selectedCount === 1 ? "foto selezionata" : "foto selezionate"}</span>
              <span className="sm:hidden">{selectedCount === 1 ? "selezionata" : "selezionate"}</span>
            </span>
            <button
              onClick={() => {
                const allIds = filtered.map((h) => h.id || h.photo_id);
                const allOn = allIds.every((id) => selected[id]);
                if (allOn) { setSelected({}); }
                else { const sel = {}; allIds.forEach((id) => { sel[id] = true; }); setSelected(sel); }
              }}
              className="text-[11.5px] text-white/75 hover:text-white underline underline-offset-2"
              data-testid="gallery-select-all"
            >
              {filtered.every((h) => selected[h.id || h.photo_id]) ? "Deseleziona" : `Tutte (${filtered.length})`}
            </button>
            <div className="flex-1 hidden sm:block" />
            <div className="flex items-center gap-1.5 ml-auto flex-wrap justify-end">
              <button
                onClick={() => {
                  // iter v26 r7 — Riprova batch: naviga all'Editor foto
                  // con la lista di id da rilavorare. FotoEditor auto-detect
                  // il mode comune (o "enhance" se misti) e parte sempre
                  // dall'original_url di ogni foto (non dalla versione lavorata).
                  const ids = Object.keys(selected).filter((k) => selected[k]);
                  if (ids.length === 0) return;
                  const qs = new URLSearchParams({ reeditMany: ids.join(",") });
                  navigate(`/dashboard/foto?${qs.toString()}`);
                }}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#7C8B6E] hover:bg-[#6B7A5D] text-white text-[12px] font-semibold transition-colors"
                data-testid="gallery-bulk-riprova"
                title="Apri le foto selezionate nell'Editor per rilavorarle"
              >
                <ArrowsClockwise size={12} weight="bold" /> Riprova
              </button>
              <button
                onClick={() => setAdHocOpen(true)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/20 text-white text-[12px] font-semibold transition-colors"
                data-testid="gallery-bulk-social"
                title="Crea post, PDF stampabile o volantino dalle foto selezionate"
              >
                <Sparkle size={12} weight="fill" /> Contenuti
              </button>
              <button
                onClick={downloadSelectedZip}
                disabled={bulkDownloading}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/20 disabled:opacity-60 text-white text-[12px] font-semibold transition-colors"
                data-testid="gallery-bulk-download"
                title="Scarica le foto selezionate"
              >
                {bulkDownloading
                  ? <><Spinner size={11} className="animate-spin" /> ZIP…</>
                  : <><DownloadSimple size={12} weight="bold" /> {selectedCount === 1 ? "Scarica" : "ZIP"}</>}
              </button>
              <button
                onClick={deleteSelected}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-red-600/80 hover:bg-red-600 text-white text-[12px] font-semibold transition-colors"
                data-testid="gallery-bulk-delete"
                title="Cancella le foto selezionate"
              >
                <Trash size={12} weight="bold" /> Cancella
              </button>
            </div>
          </div>
        </>
      )}

      {/* Picker mode: sticky CTA bar in basso · sempre visibile in modalità import */}
      {isPicker && (
        <div
          className="fixed inset-x-0 bottom-0 z-40 bg-white border-t border-[#EAE7DF] shadow-[0_-8px_24px_-12px_rgba(0,0,0,0.15)] px-4 py-3 sm:py-4"
          style={{ paddingBottom: "max(env(safe-area-inset-bottom), 12px)" }}
          data-testid="gallery-picker-bar"
        >
          <div className="max-w-3xl mx-auto flex items-center gap-3">
            <div className="flex-1 min-w-0">
              <div className="text-[11px] uppercase tracking-[0.16em] font-bold text-[#9A968B]">
                {selectedCount === 0
                  ? "Seleziona una o più foto"
                  : `${selectedCount} ${selectedCount === 1 ? "foto pronta" : "foto pronte"}`}
              </div>
              {importProperty && (
                <div className="text-[12px] text-[#1C1C1A] truncate">
                  Aggiungi a <strong>{importProperty.title || "annuncio"}</strong>
                </div>
              )}
            </div>
            {selectedCount > 0 && (
              <button
                onClick={() => setSelected({})}
                className="text-[12px] text-[#6B6A65] hover:text-[#1C1C1A] font-medium px-2 py-2"
                data-testid="gallery-picker-clear"
              >
                Azzera
              </button>
            )}
            <button
              onClick={importBatchToProperty}
              disabled={selectedCount === 0 || batchImporting}
              className="bg-[#7C8B6E] hover:bg-[#6B7A5D] disabled:opacity-50 disabled:cursor-not-allowed text-white text-[13.5px] font-semibold px-4 sm:px-5 py-2.5 rounded-full inline-flex items-center gap-1.5 shadow-sm"
              data-testid="gallery-picker-confirm"
            >
              {batchImporting ? (
                <><Spinner size={14} className="animate-spin" /> Sto aggiungendo…</>
              ) : (
                <><Plus size={14} weight="bold" /> Aggiungi {selectedCount > 0 ? selectedCount : ""}</>
              )}
            </button>
          </div>
        </div>
      )}

      {/* iter 275 round 51: Modale Social senza annuncio */}
      <AdHocSocialModal
        open={adHocOpen}
        onClose={() => setAdHocOpen(false)}
        photoIds={Object.keys(selected).filter((k) => selected[k])}
      />

      {/* iter 275: Modale conferma cancellazione — sostituisce confirm() nativo
          (bloccato silenziosamente su iOS Safari/PWA). */}
      {confirmDelete && (
        <div
          className="fixed inset-0 z-[60] bg-black/50 flex items-end sm:items-center justify-center p-4"
          onClick={() => setConfirmDelete(null)}
          data-testid="gallery-confirm-delete-backdrop"
        >
          <div
            className="bg-white rounded-2xl sm:rounded-xl w-full max-w-sm shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            data-testid="gallery-confirm-delete-modal"
          >
            <div className="p-5">
              <div className="w-11 h-11 rounded-full bg-red-50 flex items-center justify-center mb-3">
                <Trash size={20} weight="bold" className="text-red-600" />
              </div>
              <h3 className="font-editorial text-xl text-[#1C1C1A] leading-tight">
                {confirmDelete.mode === "bulk"
                  ? `Cancellare ${confirmDelete.count} foto?`
                  : "Cancellare questa foto?"}
              </h3>
              <p className="text-[13px] text-[#6B6A65] mt-2 leading-relaxed">
                L&apos;azione non è reversibile. La foto verrà rimossa anche dagli annunci in cui è usata.
              </p>
            </div>
            <div className="flex gap-2 p-3 border-t border-[#F0EFE9]">
              <button
                onClick={() => setConfirmDelete(null)}
                className="flex-1 px-4 py-2.5 rounded-md text-[13px] font-semibold text-[#1C1C1A] bg-[#F1F4ED] hover:bg-[#E5E7E0] transition-colors"
                data-testid="gallery-confirm-delete-cancel"
              >
                Annulla
              </button>
              <button
                onClick={async () => {
                  const cd = confirmDelete;
                  setConfirmDelete(null);
                  if (cd.mode === "bulk") {
                    await _doDeleteSelected();
                  } else {
                    await _doDeleteOne(cd.id, cd.source);
                  }
                }}
                className="flex-1 px-4 py-2.5 rounded-md text-[13px] font-semibold text-white bg-red-600 hover:bg-red-700 transition-colors inline-flex items-center justify-center gap-1.5"
                data-testid="gallery-confirm-delete-confirm"
              >
                <Trash size={13} weight="bold" /> Cancella
              </button>
            </div>
          </div>
        </div>
      )}

      {/* iter 275 round 35: lightbox foto estratto in
          /components/photo/GalleriaLightbox.jsx. Stato resta nel parent. */}
      <GalleriaLightbox
        item={lightboxItem}
        view={lightboxView}
        setView={setLightboxView}
        onClose={() => setLightboxItem(null)}
        properties={properties}
        shareImage={shareImage}
        setImportingId={setImportingId}
        deleteOne={deleteOne}
        onRiprova={(h) => {
          // iter v26 r8 — Riprova singolo dal lightbox: naviga all'Editor
          // foto in modalità reedit singolo. FotoEditor leggerà l'ID e
          // farà auto-detect del mode (enhance/stage/restyle/furnish_plan).
          const id = h.id || h.photo_id;
          if (!id) return;
          const qs = new URLSearchParams({ reeditMany: id });
          navigate(`/dashboard/foto?${qs.toString()}`);
        }}
        onCreaContenuti={(h) => {
          // iter v26 r9 — Crea contenuti dalla singola foto del lightbox:
          // pre-seleziona quella foto + apre l'AdHocSocialModal (stesso
          // flow del bulk "Contenuti", ma con 1 sola foto in input).
          const id = h.id || h.photo_id;
          if (!id) return;
          setSelected({ [id]: true });
          setAdHocOpen(true);
        }}
      />
    </AppShell>
  );
}

function EmptyState({ onNew }) {
  return (
    <div className="py-12 sm:py-16 flex flex-col items-center text-center" data-testid="gallery-empty">
      <div className="w-16 h-16 rounded-full bg-[#F1F4ED] flex items-center justify-center mb-4">
        <ImageIcon size={26} weight="duotone" className="text-[#7C8B6E]" />
      </div>
      <h3 className="text-lg font-serif text-[#1C1C1A]">Ancora nessuna foto</h3>
      <p className="text-[12.5px] text-[#6B6A65] mt-1 max-w-sm">
        Le foto migliorate, arredate o restylizzate appariranno qui. Inizia caricando le prime.
      </p>
      <button
        onClick={onNew}
        className="mt-4 bg-[#7C8B6E] hover:bg-[#6B7A5D] text-white text-[13px] font-semibold px-5 py-2 rounded-md"
      >
        Vai a Editor foto
      </button>
    </div>
  );
}
